import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Search, ShieldAlert, Clock, Users, RefreshCcw, Mail, Briefcase, Ban, RotateCcw, Trash2, AlertTriangle } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso).getTime();
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

// Rough signals a super admin should notice.
const flagsFor = (row) => {
  const flags = [];
  // Non-NHS / non-primary-care email domain. Highest priority — these are
  // the registrations most likely to be inappropriate.
  if (row.nhs_domain === false) {
    flags.push({ level: "danger", label: "Non-NHS domain" });
  }
  // Never logged in after registering more than 3 days ago.
  const createdDays = daysSince(row.created_at);
  const loginDays = daysSince(row.last_login_at);
  if (!row.last_login_at && createdDays !== null && createdDays > 3) {
    flags.push({ level: "warn", label: "Never signed in" });
  }
  // Empty practice — PM registered but no learners have joined.
  if ((row.learner_count || 0) === 0 && createdDays !== null && createdDays > 7) {
    flags.push({ level: "warn", label: "No learners" });
  }
  // Dormant — hasn't signed in for over 90 days.
  if (loginDays !== null && loginDays > 90) {
    flags.push({ level: "info", label: `Dormant (${loginDays}d)` });
  }
  if (row.disabled) {
    flags.push({ level: "danger", label: "Disabled" });
  }
  return flags;
};

export default function SuperAdminPMs() {
  const { isSuperAdmin, loading } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [reloading, setReloading] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [moderating, setModerating] = useState(null); // {row, action: 'disable' | 'delete'}
  const [modReason, setModReason] = useState("");
  const [modSaving, setModSaving] = useState(false);
  const [showNonNhsOnly, setShowNonNhsOnly] = useState(false);

  const load = async () => {
    setErr("");
    setReloading(true);
    try {
      const res = await api.get("/superadmin/practice-managers");
      setRows(res.data?.rows || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load practice managers");
    } finally {
      setReloading(false);
    }
  };

  const toggleOpsAccess = async (row, next) => {
    setBusyRow(row.id);
    // Optimistic update.
    setRows((prev) => (prev || []).map((r) => (r.id === row.id ? { ...r, practice_ops_access: next } : r)));
    try {
      await api.patch(`/superadmin/practice-managers/${row.id}/practice-ops-access`, {
        practice_ops_access: next,
      });
    } catch (e) {
      // Roll back on failure.
      setRows((prev) => (prev || []).map((r) => (r.id === row.id ? { ...r, practice_ops_access: !next } : r)));
      setErr(e.response?.data?.detail || "Could not update access");
    } finally {
      setBusyRow(null);
    }
  };

  const openModerate = (row, action) => {
    setModerating({ row, action });
    setModReason("");
    setErr("");
  };

  const closeModerate = () => {
    if (modSaving) return;
    setModerating(null);
    setModReason("");
  };

  const submitModerate = async () => {
    if (!moderating) return;
    const reason = modReason.trim();
    if (reason.length < 3) {
      setErr("Please give a short reason (at least 3 characters). This is written to the audit log.");
      return;
    }
    setModSaving(true);
    setErr("");
    const { row, action } = moderating;
    try {
      if (action === "delete") {
        await api.post(`/superadmin/users/${row.id}/delete`, { reason });
        setRows((prev) => (prev || []).filter((r) => r.id !== row.id));
      } else {
        await api.post(`/superadmin/users/${row.id}/disable`, { reason });
        setRows((prev) => (prev || []).map((r) => (r.id === row.id
          ? { ...r, disabled: true, disabled_reason: reason, disabled_at: new Date().toISOString() }
          : r
        )));
      }
      setModerating(null);
      setModReason("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Action failed");
    } finally {
      setModSaving(false);
    }
  };

  const enableUser = async (row) => {
    if (!window.confirm(`Re-enable ${row.email}? They'll be able to sign in again.`)) return;
    setBusyRow(row.id);
    try {
      await api.post(`/superadmin/users/${row.id}/enable`);
      setRows((prev) => (prev || []).map((r) => (r.id === row.id
        ? { ...r, disabled: false, disabled_reason: null, disabled_at: null }
        : r
      )));
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not re-enable");
    } finally {
      setBusyRow(null);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    load();
  }, [isSuperAdmin]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    let base = rows;
    if (showNonNhsOnly) {
      base = base.filter((r) => r.nhs_domain === false);
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((r) =>
      [r.name, r.email, r.practice_name, r.invite_code]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [rows, q, showNonNhsOnly]);

  if (loading) return null;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <Layout wide>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> Super admin
          </div>
          <h1 className="font-serif text-4xl text-brand-ink">Registered Practice Managers</h1>
          <p className="text-brand-secondary text-sm mt-2 max-w-2xl">
            Every account with PM (admin) role across every practice on Calside. Use this to
            monitor for accounts that were created but never used, or dormant PMs — early signals
            of an invite code being misused or an account being tested inappropriately.
          </p>
        </div>
        <button
          onClick={load}
          disabled={reloading}
          data-testid="super-pms-reload"
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-linen bg-white px-3 py-1.5 text-sm text-brand-secondary hover:text-brand-ink hover:border-brand-ink/40 disabled:opacity-50 transition-colors"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${reloading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Reload
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md min-w-[260px]">
          <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, practice or invite code…"
            data-testid="super-pms-search"
            className="w-full rounded-full border border-brand-linen bg-white pl-9 pr-4 py-2 text-sm placeholder:text-brand-muted focus:outline-none focus:border-brand-ink/40"
          />
        </div>
        <label
          data-testid="super-pms-filter-non-nhs"
          className={`inline-flex items-center gap-2 cursor-pointer select-none rounded-full px-4 py-2 text-sm border transition-colors ${showNonNhsOnly ? "bg-brand-accent text-white border-brand-accent" : "bg-white text-brand-ink border-brand-linen hover:border-brand-accent/40"}`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={showNonNhsOnly}
            onChange={(e) => setShowNonNhsOnly(e.target.checked)}
          />
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.7} />
          Non-NHS domain only
        </label>
      </div>

      {err && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="super-pms-error">
          {err}
        </div>
      )}

      {filtered === null ? (
        <div className="text-brand-muted text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-brand-linen bg-white p-8 text-center text-brand-muted text-sm">
          {q ? "No PMs match that search." : "No Practice Managers registered yet."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-brand-linen bg-white">
          <div className="hidden md:grid grid-cols-[repeat(14,minmax(0,1fr))] gap-3 border-b border-brand-linen bg-brand-linen/40 px-5 py-3 text-[11px] font-medium uppercase tracking-widest text-brand-muted">
            <div className="col-span-3">PM</div>
            <div className="col-span-3">Practice</div>
            <div className="col-span-2">Registered</div>
            <div className="col-span-2">Last sign-in</div>
            <div className="col-span-1 text-right">Learners</div>
            <div className="col-span-1 text-right" title="Grant Practice Ops access">Ops</div>
            <div className="col-span-2 text-right">Moderation</div>
          </div>
          <div>
            {filtered.map((r) => {
              const flags = flagsFor(r);
              const inner = (
                <>
                  <div className="col-span-3 min-w-0">
                    <div className="text-brand-ink font-medium truncate" title={r.name || ""}>{r.name || "—"}</div>
                    <div className="text-brand-muted text-xs truncate flex items-center gap-1" title={r.email || ""}>
                      <Mail className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                      {r.email || "—"}
                    </div>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <div className="text-brand-secondary truncate" title={r.practice_name || ""}>{r.practice_name}</div>
                    {r.invite_code && (
                      <div className="text-brand-muted text-xs font-mono truncate" title={`Invite code: ${r.invite_code}`}>
                        {r.invite_code}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-brand-secondary text-xs">
                    <div>{fmt(r.created_at)}</div>
                    {daysSince(r.created_at) !== null && (
                      <div className="text-brand-muted">{daysSince(r.created_at)}d ago</div>
                    )}
                  </div>
                  <div className="col-span-2 text-brand-secondary text-xs">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                      {fmt(r.last_login_at)}
                    </div>
                    {daysSince(r.last_login_at) !== null && (
                      <div className="text-brand-muted">{daysSince(r.last_login_at)}d ago</div>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="inline-flex items-center gap-1 text-brand-secondary">
                      <Users className="w-3 h-3" strokeWidth={1.5} />
                      {r.learner_count}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col items-end gap-1">
                    <label
                      className="inline-flex items-center gap-1 cursor-pointer select-none"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleOpsAccess(r, !r.practice_ops_access); }}
                      title={r.practice_ops_access ? "Revoke Practice Ops access" : "Grant Practice Ops access"}
                      data-testid={`super-pm-ops-toggle-${r.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.practice_ops_access}
                        readOnly
                        disabled={busyRow === r.id}
                        className="rounded border-brand-linen text-brand-ink focus:ring-brand-ink cursor-pointer"
                      />
                      <Briefcase className={`w-3 h-3 ${r.practice_ops_access ? "text-brand-ink" : "text-brand-muted"}`} strokeWidth={1.5} />
                    </label>
                    {flags.map((f, i) => (
                      <span
                        key={i}
                        className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border whitespace-nowrap ${
                          f.level === "danger"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : f.level === "warn"
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-brand-linen/50 text-brand-secondary border-brand-linen"
                        }`}
                        data-testid={`super-pm-flag-${r.id}-${i}`}
                      >
                        {f.label}
                      </span>
                    ))}
                  </div>
                  <div
                    className="col-span-2 flex flex-col items-end gap-1.5"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  >
                    {r.disabled ? (
                      <button
                        type="button"
                        data-testid={`super-pm-enable-${r.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); enableUser(r); }}
                        disabled={busyRow === r.id}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-brand-secondary px-2.5 py-1 text-brand-secondary hover:bg-brand-secondary hover:text-white disabled:opacity-50 transition-colors"
                        title="Re-enable this account"
                      >
                        <RotateCcw className="w-3 h-3" strokeWidth={1.7} /> Enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`super-pm-disable-${r.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModerate(r, "disable"); }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-amber-500/50 px-2.5 py-1 text-amber-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors"
                        title="Soft-disable this account (reversible)"
                      >
                        <Ban className="w-3 h-3" strokeWidth={1.7} /> Disable
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`super-pm-delete-${r.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModerate(r, "delete"); }}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-red-400 px-2.5 py-1 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                      title="Permanently delete this account and all their data"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.7} /> Delete
                    </button>
                  </div>
                </>
              );
              const commonCls = "hidden md:grid grid-cols-[repeat(14,minmax(0,1fr))] gap-3 border-b border-brand-linen last:border-b-0 px-5 py-4 text-sm transition-colors";
              // Mobile card view — stacked, legible, with the same moderation
              // actions. Rendered below md breakpoint only.
              const mobileCard = (
                <div className="md:hidden border-b border-brand-linen last:border-b-0 p-4" data-testid={`super-pm-mobile-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-brand-ink font-medium truncate">{r.name || "—"}</div>
                      <div className="text-brand-muted text-xs truncate flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                        {r.email || "—"}
                      </div>
                      <div className="text-brand-secondary text-sm mt-2 truncate">{r.practice_name}</div>
                      {r.invite_code && (
                        <div className="text-brand-muted text-[11px] font-mono mt-0.5 truncate">{r.invite_code}</div>
                      )}
                    </div>
                    {r.practice_id && (
                      <Link
                        to={`/super/pms/${r.practice_id}`}
                        className="text-[11px] uppercase tracking-wider text-brand-muted border border-brand-linen rounded-full px-2.5 py-1 whitespace-nowrap flex-shrink-0"
                      >
                        Open →
                      </Link>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-muted">Registered</div>
                      <div className="text-brand-ink">{daysSince(r.created_at) !== null ? `${daysSince(r.created_at)}d ago` : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-muted">Last sign-in</div>
                      <div className="text-brand-ink">{daysSince(r.last_login_at) !== null ? `${daysSince(r.last_login_at)}d ago` : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-muted">Learners</div>
                      <div className="text-brand-ink font-medium">{r.learner_count || 0}</div>
                    </div>
                  </div>
                  {flags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {flags.map((f, i) => (
                        <span
                          key={i}
                          className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border whitespace-nowrap ${
                            f.level === "danger"
                              ? "bg-red-50 text-red-700 border-red-200"
                              : f.level === "warn"
                              ? "bg-amber-50 text-amber-800 border-amber-200"
                              : "bg-brand-linen/50 text-brand-secondary border-brand-linen"
                          }`}
                        >
                          {f.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center flex-wrap gap-2 mt-3 pt-3 border-t border-brand-linen">
                    {r.disabled ? (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); enableUser(r); }}
                        disabled={busyRow === r.id}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-brand-secondary px-3 py-1.5 text-brand-secondary disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" strokeWidth={1.7} /> Enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModerate(r, "disable"); }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-amber-500/50 px-3 py-1.5 text-amber-700"
                      >
                        <Ban className="w-3 h-3" strokeWidth={1.7} /> Disable
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModerate(r, "delete"); }}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider rounded-full border border-red-400 px-3 py-1.5 text-red-700"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.7} /> Delete
                    </button>
                  </div>
                </div>
              );
              if (r.practice_id) {
                return (
                  <React.Fragment key={r.id}>
                    {mobileCard}
                    <Link
                      to={`/super/pms/${r.practice_id}`}
                      data-testid={`super-pm-row-${r.id}`}
                      className={`${commonCls} hover:bg-brand-linen/30 cursor-pointer`}
                      title="Open practice detail"
                    >
                      {inner}
                    </Link>
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={r.id}>
                  {mobileCard}
                  <div
                    data-testid={`super-pm-row-${r.id}`}
                    className={`${commonCls} hover:bg-brand-linen/20`}
                  >
                    {inner}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 text-xs text-brand-muted">
        Showing <span className="text-brand-ink font-medium">{filtered ? filtered.length : 0}</span>
        {rows && filtered && filtered.length !== rows.length && (
          <> of <span className="text-brand-ink font-medium">{rows.length}</span></>
        )}
        {" "}Practice Manager account(s).
      </div>

      {moderating && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 flex items-center justify-center p-4" data-testid="super-pm-mod-modal">
          <div className="bg-white rounded-3xl shadow-card max-w-md w-full overflow-hidden">
            <div className="p-5 border-b border-brand-linen">
              <div className="flex items-center gap-2">
                {moderating.action === "delete" ? (
                  <Trash2 className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                ) : (
                  <Ban className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                )}
                <h3 className="font-serif text-xl text-brand-ink">
                  {moderating.action === "delete" ? "Permanently delete account" : "Disable account"}
                </h3>
              </div>
              <p className="text-sm text-brand-body mt-2">
                <span className="font-medium text-brand-ink">{moderating.row.name || "(no name)"}</span>
                {" "}· <span className="text-brand-muted">{moderating.row.email}</span>
              </p>
              <p className="text-sm text-brand-body mt-1">
                Practice: <span className="text-brand-ink">{moderating.row.practice_name}</span>
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div className={`rounded-2xl border p-4 text-sm ${moderating.action === "delete" ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                {moderating.action === "delete" ? (
                  <>
                    <div className="font-medium mb-1">This cannot be undone.</div>
                    <ul className="list-disc list-inside space-y-0.5 text-[13px]">
                      <li>The user record is deleted immediately.</li>
                      <li>All their progress, quiz attempts and course assignments are removed.</li>
                      <li>The email can register again later as a fresh account.</li>
                      <li>The reason and your email are kept in the audit log.</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <div className="font-medium mb-1">Reversible — the account is kept but blocked.</div>
                    <ul className="list-disc list-inside space-y-0.5 text-[13px]">
                      <li>The user can't sign in. Existing sessions are rejected.</li>
                      <li>They'll see your reason on their login attempt.</li>
                      <li>Click <em>Enable</em> any time to restore access.</li>
                    </ul>
                  </>
                )}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-brand-muted mb-1">Reason (required · goes to audit log)</label>
                <textarea
                  data-testid="super-pm-mod-reason"
                  value={modReason}
                  onChange={(e) => setModReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Non-NHS domain / spam registration / duplicate account"
                  maxLength={500}
                  className="w-full rounded-2xl border border-brand-linen bg-brand-bone p-3 text-sm focus:outline-none focus:border-brand-ink"
                />
                <div className="text-[11px] text-brand-muted mt-1">
                  Minimum 3 characters. {modReason.length}/500
                </div>
              </div>

              {err && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>
              )}
            </div>

            <div className="p-5 border-t border-brand-linen flex items-center gap-3">
              <button
                type="button"
                data-testid="super-pm-mod-cancel"
                onClick={closeModerate}
                disabled={modSaving}
                className="ml-auto border border-brand-linen rounded-full px-5 py-2 text-sm hover:bg-brand-bone disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="super-pm-mod-submit"
                onClick={submitModerate}
                disabled={modSaving || modReason.trim().length < 3}
                className={`rounded-full px-6 py-2 text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  moderating.action === "delete"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-amber-600 hover:bg-amber-700"
                }`}
              >
                {modSaving ? "Saving…" : moderating.action === "delete" ? "Delete permanently" : "Disable account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
